import type { Observable } from 'rxjs';
import type { Uuid } from '@common/types';
import type { Widgets } from '@common/enums';
import type { NormalizedBlockEntity, DenormalizedBlockEntity, NormalizedZeroBlockEntity } from '@web/types';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import {
    blockInstance,
    specificBlockInstance,
    zeroBlockInstance,
} from '@web/store/page-structure/blocks/blocks.models';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';

import BlocksActions from './blocks.actions';
import * as BlocksSelectors from './blocks.selectors';

@Injectable()
export class BlocksFacade {
    private readonly store = inject(Store);
    private sectionsFacade = inject(SectionsFacade);

    /**
     * Combine pieces of state using createSelector,
     * and expose them as observables through the facade.
     */
    public readonly blocks$ = this.store.select(BlocksSelectors.selectAllBlocks);
    public readonly blocksEntities$ = this.store.select(BlocksSelectors.selectBlocksEntities);
    public readonly structure$ = this.store.select(BlocksSelectors.selectStructure);
    public readonly lastBlockIndex$: Observable<number> = this.store.select(BlocksSelectors.selectLastBlockIndex);
    public readonly columnConnectedToList$ = this.store.select(BlocksSelectors.selectColumnConnectedToList);
    public readonly specificColumnConnectedToList$ = this.store.select(
        BlocksSelectors.selectSpecificColumnConnectedToList
    );
    public readonly sectionConnectedToList$ = this.store.select(BlocksSelectors.selectSectionConnectedToList);
    public readonly specificSectionConnectedToList$ = this.store.select(
        BlocksSelectors.selectSpecificSectionConnectedToList
    );

    /**
     * Use the initialization action to perform one
     * or more tasks in your Effects.
     */
    public init(blocks: NormalizedBlockEntity[]): void {
        this.store.dispatch(BlocksActions.init({ blocks }));
    }

    public getBlockInstanceValue(): NormalizedBlockEntity {
        return blockInstance();
    }

    public getSpecificBlockInstanceValue(): NormalizedBlockEntity {
        return specificBlockInstance();
    }

    public getZeroBlockInstanceValue(): NormalizedZeroBlockEntity {
        return zeroBlockInstance();
    }

    public addBlock(block: DenormalizedBlockEntity, index: number): void {
        this.store.dispatch(BlocksActions.addBlock({ block, index }));
    }

    public createBlock(index: number, widget?: Widgets): void {
        let widgetCorrelationData = null;
        if (widget) {
            widgetCorrelationData = { widget };
        }
        this.store.dispatch(BlocksActions.createBlock({ index, widgetCorrelationData }));
    }

    public createSpecificBlock(index: number): void {
        this.store.dispatch(BlocksActions.createSpecificBlock({ index }));
    }

    public createZeroBlock(index: number): void {
        this.store.dispatch(BlocksActions.createZeroBlock({ index }));
    }

    public copyBlock(initialBlock: DenormalizedBlockEntity, index: number): void {
        this.store.dispatch(BlocksActions.copyBlock({ initialBlock, index }));
    }

    public copyToPage(initialBlock: DenormalizedBlockEntity): void {
        this.store.dispatch(BlocksActions.copyBlockToPage({ initialBlock }));
    }

    public copyBlockToPageRequest(pageIds: number[], block: DenormalizedBlockEntity): void {
        this.store.dispatch(BlocksActions.copyBlockToPageRequest({ pageIds, block }));
    }

    public updateBlockProperties(id: Uuid, changes: Partial<NormalizedBlockEntity>): void {
        this.store.dispatch(BlocksActions.updateBlockProperties({ update: { id, changes } }));
    }

    public removeBlock(block: DenormalizedBlockEntity, index: number, skipHistory: boolean = false): void {
        this.store.dispatch(BlocksActions.removeBlock({ block, index, skipHistory }));

        this.sectionsFacade.removeManySections(block.children);
    }

    public moveUp(index: number, skipHistory: boolean = false): void {
        this.store.dispatch(BlocksActions.moveUp({ index, skipHistory }));
    }

    public moveDown(index: number, skipHistory: boolean = false): void {
        this.store.dispatch(BlocksActions.moveDown({ index, skipHistory }));
    }

    public hideBlock(blockId: Uuid, pageId: Uuid | number): void {
        this.store.dispatch(BlocksActions.hideBlock({ blockId, pageId }));
    }

    public showBlock(blockId: Uuid, pageId: Uuid | number): void {
        this.store.dispatch(BlocksActions.showBlock({ blockId, pageId }));
    }

    public get blocksIds$(): Observable<string[]> {
        return this.store.select(BlocksSelectors.selectBlocksIds) as Observable<string[]>;
    }

    public blockById$(id: Uuid): Observable<NormalizedBlockEntity> {
        return this.store.select(BlocksSelectors.selectBlockById(id));
    }

    public isBlockFirst$(id: Uuid): Observable<boolean> {
        return this.store.select(BlocksSelectors.isBlockFirst(id));
    }

    public isBlockLast$(id: Uuid): Observable<boolean> {
        return this.store.select(BlocksSelectors.isBlockLast(id));
    }

    public blockStructureEntityById$(id: Uuid): Observable<DenormalizedBlockEntity> {
        return this.store.select(BlocksSelectors.selectBlockStructureEntityById(id));
    }
}
